import { Component, OnDestroy, inject, signal } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from 'src/app/shared/services/auth/auth.service';
import { HeaderAppComponent } from '../../headers/header-app/header-app.component';
import { FormTemplateComponent } from '../../../shared/screen-templates/form-template/form-template.component';
import { PayeePasswordFormFieldComponent } from '../../../shared/payee-form-fields/payee-password-form-field/payee-password-form-field.component';
import { MatError } from '@angular/material/form-field';
import { PayeeButtonComponent } from '../../../shared/payee-buttons/payee-button/payee-button.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

type NewPasswordControls = {
  password: FormControl<string | null>;
  passwordConfirmation: FormControl<string | null>;
};

@Component({
  selector: 'app-new-password',
  templateUrl: './new-password.component.html',
  styleUrls: ['./new-password.component.scss'],
  imports: [
    HeaderAppComponent,
    FormTemplateComponent,
    FormsModule,
    ReactiveFormsModule,
    PayeePasswordFormFieldComponent,
    MatError,
    PayeeButtonComponent,
    MatProgressSpinner,
  ],
})
export class NewPasswordComponent implements OnDestroy {
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private route = inject(ActivatedRoute);
  private snackBar = inject(MatSnackBar);

  private readonly subscriptionKiller = new Subject<void>();

  readonly passwordForm: FormGroup<NewPasswordControls>;
  readonly email: string;
  readonly token: string;

  readonly loading = signal(false);

  constructor() {
    this.email = this.route.snapshot.queryParams.email;
    this.token = this.route.snapshot.params.token;

    this.passwordForm = this.fb.group<NewPasswordControls>({
      password: this.fb.control('', [
        Validators.required,
        Validators.minLength(6),
      ]),
      passwordConfirmation: this.fb.control('', [
        Validators.required,
        Validators.minLength(6),
        this.passwordConfirmationValidator,
      ]),
    });

    this.password.valueChanges
      .pipe(takeUntil(this.subscriptionKiller))
      .subscribe({
        next: () => {
          this.passwordConfirmation.updateValueAndValidity();
        },
      });
  }

  get password(): FormControl<string | null> {
    return this.passwordForm.controls.password;
  }
  get passwordConfirmation(): FormControl<string | null> {
    return this.passwordForm.controls.passwordConfirmation;
  }

  ngOnDestroy(): void {
    this.subscriptionKiller.next();
    this.subscriptionKiller.complete();
  }

  async setNewPassword(): Promise<void> {
    if (this.passwordForm.invalid) {
      this.password.markAsDirty();
      this.passwordConfirmation.markAsDirty();
      return;
    }

    try {
      this.loading.set(true);

      await this.authService.resetPassword({
        token: this.token,
        password: this.password.value!,
        password_confirmation: this.passwordConfirmation.value!,
        email: this.email,
      });
    } catch (error) {
      console.error(error);
      this.snackBar.open('Valami hiba történt!', '', {
        duration: 15000,
      });
    } finally {
      this.loading.set(false);
    }
  }

  private readonly passwordConfirmationValidator = (
    control: AbstractControl,
  ) => {
    const value = control.value;
    if (!value) {
      return null;
    }
    if (value !== this.password.value) {
      return { passwordsDontMatch: true };
    }
    return null;
  };
}
